import React, { useEffect, useRef, useState } from "react";
import { Button, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import PictureInPictureAltIcon from "@mui/icons-material/PictureInPictureAlt";
import UndoIcon from "@mui/icons-material/Undo";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import { embeddedFrom, embeddedVersion, isVreDocument } from "@wooglobe/vre-editor";
import { VideoSource } from "../../Enums/enums";

const INDIGO = "#4338ca";
const AMBER = "#b45309";

/** A sibling platform's saved editor document, or null when it has none. */
export const savedEditOf = (config) => {
  if (config?.source !== VideoSource.EDITED) return null;
  const entry = Array.isArray(config.renderPayload) ? config.renderPayload[0] : null;
  return entry && entry.engine === "vre" && isVreDocument(entry.document) ? entry.document : null;
};

/**
 * A short fingerprint of a saved edit (32-bit FNV-1a of its JSON), stored with
 * a copy so the tab can tell when the source has been saved differently since.
 * The same document always gives the same fingerprint.
 */
export const editVersion = (doc) => {
  const text = JSON.stringify(doc ?? null);
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
};

/**
 * True when `doc` holds a copy of another platform's edit and that platform
 * (found in `siblings`, `{platform, config}`) has saved a different edit since.
 * A copy made before versions were stored cannot tell, and says false.
 */
export const isCopyOutdated = (doc, siblings) => {
  if (!doc) return false;
  const from = embeddedFrom(doc);
  const copied = embeddedVersion(doc);
  if (!from || !copied) return false;
  const now = savedEditOf(siblings.find((s) => s.platform === from)?.config);
  return Boolean(now && editVersion(now) !== copied);
};

/**
 * "Copy edit from…" for the open tab: another platform's saved edit of this
 * WGID — its video, watermark, texts and captions — goes inside this tab's
 * template, in place of the template's video. This tab keeps its own template
 * and renders on its own; Duplicate is untouched.
 */
export default function CopyEditControl({ item, siblings, copiedFrom, outdated = false, onCopy, onRemove, onPanelChange }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => onPanelChange(open), [open, onPanelChange]);
  useEffect(() => () => onPanelChange(false), [onPanelChange]);

  const sources = siblings.filter((s) => s.platform !== item.platform && savedEditOf(s.config));
  const disabled = sources.length === 0 && !copiedFrom;

  const pick = (fn) => {
    setOpen(false);
    fn();
  };

  const tone = outdated ? AMBER : INDIGO;

  return (
    <>
      {outdated && (
        <Tooltip title={`${copiedFrom}'s edit was saved again after it was copied here. Copy again to bring the changes in — this replaces the copied layers.`}>
          <Button
            size="small"
            variant="contained"
            disableElevation
            startIcon={<SyncProblemIcon sx={{ fontSize: "14px !important" }} />}
            onClick={() => onCopy(copiedFrom)}
            data-testid="batch-copy-edit-outdated"
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 700, minHeight: 26, height: 26, py: 0, px: 1, fontSize: 11.5, bgcolor: "#fef3c7", color: AMBER, border: `1px solid ${alpha(AMBER, 0.5)}`, "&:hover": { bgcolor: "#fde68a" } }}
          >
            {copiedFrom} changed · Copy again
          </Button>
        </Tooltip>
      )}
      <Button
        ref={anchorRef}
        size="small"
        variant="outlined"
        startIcon={<PictureInPictureAltIcon sx={{ fontSize: "13px !important" }} />}
        onClick={() => setOpen(true)}
        disabled={disabled}
        title={disabled ? `No other ${item.wgid} platform has a saved edit to copy yet` : undefined}
        aria-haspopup="menu"
        data-testid="batch-copy-edit"
        sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, minHeight: 26, height: 26, py: 0, px: 1, fontSize: 11.5, borderColor: alpha(tone, 0.4), color: tone }}
      >
        {copiedFrom ? `Edit from ${copiedFrom}` : "Copy edit from…"}
      </Button>

      <Menu
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { mt: 1, width: 340, maxWidth: "calc(100vw - 32px)", borderRadius: 2 } }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 0.5, pb: 1 }}>
          Put another platform's saved edit — video, watermark, texts and captions — inside this {item.platform} template's video box.
        </Typography>
        {sources.map((s) => (
          <MenuItem key={s.platform} onClick={() => pick(() => onCopy(s.platform))} data-testid={`batch-copy-edit-${s.platform}`}>
            <ListItemIcon>
              <PictureInPictureAltIcon fontSize="small" sx={{ color: INDIGO }} />
            </ListItemIcon>
            <ListItemText
              primary={`${s.platform}'s edit`}
              secondary={
                copiedFrom === s.platform
                  ? outdated
                    ? "Changed since this copy — copy again (replaces the current copy)"
                    : "Copy again (replaces the current copy)"
                  : "Its last saved version"
              }
              primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
            />
          </MenuItem>
        ))}
        {copiedFrom && [
          sources.length > 0 && <Divider key="d" />,
          <MenuItem key="remove" onClick={() => pick(onRemove)} data-testid="batch-copy-edit-remove">
            <ListItemIcon>
              <UndoIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Remove the copied edit" secondary={`Puts the ${item.platform} template's own video back`} primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
          </MenuItem>,
        ]}
      </Menu>
    </>
  );
}
